// The pending door — the authorization a person walked away from to meet their
// netti, carried into the dashboard so they can finish it from there.
//
// Someone who adds innernet inside Claude gets bounced to the consent page. If
// they make their account there they are sent through the birth and, when
// they keep the creature, straight into their dashboard — which means the
// tool that started all this is still waiting at the door. The grant's
// parameters are public by design (the client id, the redirect, the PKCE
// challenge; the verifier stays with the tool), so they ride in a cookie on
// the shared domain, the same way the newborn does, and the dashboard offers
// "let Claude in" until it is done or waved off.
//
// The CLI door is the same shape with one field: where to go to finish pairing.
//
// Client-safe: no React by default; the hook at the bottom is for the surfaces
// that show the door. Pure helpers are tested.

import { useCallback, useEffect, useState } from 'react'

export const PENDING_DOOR_COOKIE = 'innernet_pending_door'
/** a tool's OAuth flow does not wait forever; after this the door is gone. */
export const PENDING_DOOR_TTL_MS = 30 * 60 * 1000

export interface PendingGrant {
  client_id: string
  client_name: string
  redirect_uri: string
  state: string
  code_challenge: string
  code_challenge_method: string
  scope: string
}

export type PendingDoor =
  | { kind: 'grant'; grant: PendingGrant; at: number }
  | { kind: 'cli'; href: string; at: number }

/** The grant a consent url carries, or null when it is not one / is incomplete. */
export function grantFromConsentUrl(next: string): PendingGrant | null {
  let u: URL
  try {
    u = new URL(next, 'https://innernet.live')
  } catch {
    return null
  }
  if (!u.pathname.startsWith('/oauth/consent')) return null
  const p = u.searchParams
  const client_id = (p.get('client_id') ?? '').trim()
  const redirect_uri = (p.get('redirect_uri') ?? '').trim()
  if (!client_id || !redirect_uri) return null
  return {
    client_id: client_id.slice(0, 500),
    client_name: (p.get('client_name') ?? '').trim().slice(0, 80) || 'your ai',
    redirect_uri: redirect_uri.slice(0, 1000),
    state: (p.get('state') ?? '').slice(0, 500),
    code_challenge: (p.get('code_challenge') ?? '').slice(0, 200),
    code_challenge_method: (p.get('code_challenge_method') ?? 'S256').slice(0, 10),
    scope: (p.get('scope') ?? 'mcp:read mcp:write').slice(0, 200),
  }
}

/** Narrow whatever came out of the cookie into a door, or null. Expired = null. */
export function parsePendingDoor(raw: unknown, now = Date.now()): PendingDoor | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const at = typeof r.at === 'number' ? r.at : NaN
  if (!Number.isFinite(at) || now - at > PENDING_DOOR_TTL_MS || now - at < -60_000) return null
  if (r.kind === 'grant' && r.grant && typeof r.grant === 'object') {
    const g = r.grant as Record<string, unknown>
    const s = (k: string, max: number, fallback = '') =>
      typeof g[k] === 'string' ? (g[k] as string).slice(0, max) : fallback
    const grant: PendingGrant = {
      client_id: s('client_id', 500),
      client_name: s('client_name', 80, 'your ai') || 'your ai',
      redirect_uri: s('redirect_uri', 1000),
      state: s('state', 500),
      code_challenge: s('code_challenge', 200),
      code_challenge_method: s('code_challenge_method', 10, 'S256') || 'S256',
      scope: s('scope', 200, 'mcp:read mcp:write') || 'mcp:read mcp:write',
    }
    if (!grant.client_id || !grant.redirect_uri) return null
    return { kind: 'grant', grant, at }
  }
  if (r.kind === 'cli' && typeof r.href === 'string' && r.href.startsWith('/api/cli/pair')) {
    return { kind: 'cli', href: r.href.slice(0, 2000), at }
  }
  return null
}

/** The name on the door, for a sentence. */
export function doorName(door: PendingDoor): string {
  return door.kind === 'grant' ? door.grant.client_name : 'the terminal'
}

// ── the cookie ───────────────────────────────────────────────────────────────
// A cookie rather than localStorage because the dashboard may live on another
// origin (yours.innernet.live); the session already crosses on COOKIE_DOMAIN
// and the door rides the same way.

function cookieTail(): string {
  const domain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  return `; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}${secure}`
}

/** a door as written — the clock is stamped here. (A plain `Omit` on the
 *  union would collapse it to the common keys.) */
export type PendingDoorInput = { kind: 'grant'; grant: PendingGrant } | { kind: 'cli'; href: string }

export function writePendingDoor(door: PendingDoorInput): void {
  if (typeof document === 'undefined') return
  try {
    const value = encodeURIComponent(JSON.stringify({ ...door, at: Date.now() }))
    document.cookie = `${PENDING_DOOR_COOKIE}=${value}; max-age=${Math.floor(PENDING_DOOR_TTL_MS / 1000)}${cookieTail()}`
  } catch {
    /* cookies off — the tool's own retry is the fallback */
  }
}

export function readPendingDoor(now = Date.now()): PendingDoor | null {
  if (typeof document === 'undefined') return null
  try {
    const c = document.cookie.split('; ').find((x) => x.startsWith(`${PENDING_DOOR_COOKIE}=`))
    if (!c) return null
    return parsePendingDoor(JSON.parse(decodeURIComponent(c.slice(PENDING_DOOR_COOKIE.length + 1))), now)
  } catch {
    return null
  }
}

export function clearPendingDoor(): void {
  if (typeof document === 'undefined') return
  try {
    document.cookie = `${PENDING_DOOR_COOKIE}=; max-age=0${cookieTail()}`
  } catch {
    /* fine */
  }
}

/** Finish a grant from wherever the person is: the same POST the consent page
 *  makes. Resolves to the url the tool is waiting on, or null. */
export async function approveGrant(grant: PendingGrant): Promise<string | null> {
  try {
    const res = await fetch('/api/oauth/approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: grant.client_id,
        redirect_uri: grant.redirect_uri,
        state: grant.state,
        code_challenge: grant.code_challenge,
        code_challenge_method: grant.code_challenge_method,
        scope: grant.scope,
      }),
    })
    if (!res.ok) return null
    const data = (await res.json()) as { redirect?: string }
    return typeof data.redirect === 'string' ? data.redirect : null
  } catch {
    return null
  }
}

// ── the hook ─────────────────────────────────────────────────────────────────

export function usePendingDoor(): {
  door: PendingDoor | null
  busy: boolean
  /** finish it: a grant is approved and the page leaves for the tool; the
   *  cli door navigates to the pairing url. */
  finish: () => Promise<void>
  /** wave it off — the cookie goes, the tool times out on its own. */
  dismiss: () => void
  /** failed to finish (the tool may have stopped waiting). */
  error: string | null
} {
  const [door, setDoor] = useState<PendingDoor | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // read after mount (the cookie is a browser fact, never SSR), and again when
  // the tab comes back — the door may have been finished elsewhere.
  useEffect(() => {
    const read = () => setDoor(readPendingDoor())
    read()
    const onVis = () => {
      if (!document.hidden) read()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  const dismiss = useCallback(() => {
    clearPendingDoor()
    setDoor(null)
  }, [])

  const finish = useCallback(async () => {
    if (!door || busy) return
    setBusy(true)
    setError(null)
    if (door.kind === 'cli') {
      clearPendingDoor()
      window.location.href = door.href
      return
    }
    const redirect = await approveGrant(door.grant)
    if (!redirect) {
      setBusy(false)
      setError(`${door.grant.client_name} could not be let in from here. add innernet in it again and it will ask afresh.`)
      clearPendingDoor()
      setDoor(null)
      return
    }
    clearPendingDoor()
    window.location.href = redirect
  }, [door, busy])

  return { door, busy, finish, dismiss, error }
}
